import { useFormikContext } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useCompanyFormats from '../../../../../../utilities/useCompanyFormats';
import useSelectedFinderVehicles from '../../../shared/useSelectedFinderVehicles';
import useSelectedVariants from '../../../shared/useSelectedVariants';
import { type FinanceProductFormValues } from '../../index';
import { type LeaseTableRecord } from './shared';
import useColumns from './useColumns';
import useRecords from './useRecords';
import useTerms from './useTerms';
import useVehicleReferenceParameterRecords from './useVehicleReferenceParameterRecords';

const useLeaseReferenceData = ({ isDisabled = false }: { isDisabled?: boolean }) => {
    const { t } = useTranslation(['financeProductDetails']);
    const { formatAmount } = useCompanyFormats();

    const { values, setFieldValue } = useFormikContext<FinanceProductFormValues>();

    const { variantSuiteIds, lease: leaseSettings, term: termSettings } = values;

    const terms = useTerms(termSettings);

    const variants = useSelectedVariants(variantSuiteIds);
    const finderVehicles = useSelectedFinderVehicles(variantSuiteIds);

    const variantRecords = useRecords(variants, leaseSettings, terms);
    const finderVehicleRecords = useRecords(finderVehicles, leaseSettings, terms);
    const vehicleReferenceParameterRecords = useVehicleReferenceParameterRecords({
        leaseSettings,
        formatAmount,
        vehicleReferenceParameters: values.vehicleReferenceParameters,
        terms,
    });

    const onUpdate = useCallback(
        (record: LeaseTableRecord) => {
            // clone current records
            const current = [
                ...variantRecords,
                ...finderVehicleRecords,
                ...vehicleReferenceParameterRecords.variant,
                ...vehicleReferenceParameterRecords.finderVehicle,
            ].filter(Boolean);

            // find target index
            const { variantSuiteId: targetId } = record;
            const index = current.findIndex(({ variantSuiteId }) => variantSuiteId === targetId);

            // update target
            current.splice(index, 1, record);
            const updated = current
                .flatMap(
                    (
                        record // map terms to table
                    ) =>
                        terms.map(term => ({
                            variantSuiteId: record.variantSuiteId,
                            term,
                            value: record[`term${term}`],
                        }))
                )
                .filter(({ value }) => Number.isFinite(value));

            // then update formik
            setFieldValue('lease.table', updated);
        },
        [
            variantRecords,
            finderVehicleRecords,
            vehicleReferenceParameterRecords.variant,
            vehicleReferenceParameterRecords.finderVehicle,
            setFieldValue,
            terms,
        ]
    );

    const variantColumns = useColumns(
        t('financeProductDetails:tables.lease.columnTitles.variantTerm'),
        terms,
        isDisabled,
        onUpdate
    );

    const finderVehicleColumns = useColumns(
        t('financeProductDetails:tables.lease.columnTitles.finderVehicleTerm'),
        terms,
        isDisabled,
        onUpdate
    );

    return useMemo(
        () => ({
            variantColumns,
            finderVehicleColumns,
            variantRecords: [...variantRecords, ...vehicleReferenceParameterRecords.variant],
            finderVehicleRecords: [...finderVehicleRecords, ...vehicleReferenceParameterRecords.finderVehicle],
        }),
        [
            variantColumns,
            finderVehicleColumns,
            variantRecords,
            vehicleReferenceParameterRecords.variant,
            vehicleReferenceParameterRecords.finderVehicle,
            finderVehicleRecords,
        ]
    );
};

export default useLeaseReferenceData;
